<template>
  <div class="grafico-wrapper">
    <div class="grafico-barras-container">
      <div :id="id" class="grafico-barras-chart"></div>
    </div>
    <EixoXFlutuante
      :valores="eixoXValores"
      :margem-esquerda="margemEsquerda"
    />
  </div>
</template>

<script>
import { formatarUnidades } from '@/produto/common/functions/numeric';
import loadChart from './loadChart';
import EixoXFlutuante from './EixoXFlutuante';

export default {
  name: 'GraficoBarrasHorizontais',
  components: {
    EixoXFlutuante,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    categoryField: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    valueField: {
      type: String,
      required: true,
    },
    dados: {
      type: Array,
      required: true,
    },
    showExport: {
      type: Boolean,
      default: false,
    },
    colors: {
      type: Array,
      default: () => ['#4285f4', '#ff9901'],
    },
  },
  watch: {
    dados() {
      this.montarGrafico();
    },
  },
  data() {
    return {
      isReady: false,
      eixoXValores: [],
      margemEsquerda: 0,
      chartInstance: null,
    };
  },
  methods: {
    getBalloonText() {
      return [
        "<div style='text-align:left; font-size:14px'>",
        '<span>[[category]]</span>',
        '<br>',
        '<span>R$ [[value]]</span>',
        '</div>',
      ].join('');
    },
    resetarScroll() {
      this.$nextTick(() => {
        const chartElement = document.querySelector(`#${this.id}`);
        if (chartElement) {
          const container = chartElement.closest('.grafico-barras-container');
          if (container) {
            container.scrollTop = 0;
          }
        }
      });
    },
    limparInstanciaAnterior() {
      if (this.chartInstance) {
        if (this.chartInstance.clear) {
          this.chartInstance.clear();
        }
        if (this.chartInstance.removeListener) {
          this.chartInstance.removeListener('rendered', this.handleRendered);
        }
        this.chartInstance = null;
      }
    },
    montarGrafico() {
      if (typeof AmCharts === 'undefined' || !this.isReady) return;
      if (!this.dados || this.dados.length === 0) return;

      this.resetarScroll();
      this.limparInstanciaAnterior();

      const barHeight = 28.5;
      const minHeight = 320;
      const calculatedHeight = Math.max(minHeight, this.dados.length * barHeight + 100);
      const titleColor = '#1B5499';
      const temPercentual = (this.dados || []).some((d) => d && d.percentual !== undefined && d.percentual !== null);
      const maxVal = Math.max(0, ...this.dados.map((d) => Number(d[this.valueField]) || 0));

      const chart = {
        id: `horizontal-bar-chart-${new Date().getMilliseconds()}`,
        type: 'serial',
        theme: 'none',
        rotate: true,
        dataProvider: this.dados,
        categoryField: this.categoryField,
        marginRight: 90,
        columnSpacing: 10,
        titles: [{
          text: this.title,
          size: 24,
          color: titleColor,
          bold: true,
        }],
        valueAxes: [{
          position: 'bottom',
          axisAlpha: 0,
          gridAlpha: 0,
          axisThickness: 0,
          gridThickness: 0,
          labelsEnabled: false,
          maximum: maxVal > 0 ? maxVal * 1.4 : undefined,
          labelFunction: (value) => formatarUnidades(value, 2),
        }],
        categoryAxis: {
          gridPosition: 'start',
          axisAlpha: 0,
          gridAlpha: 0,
          position: 'left',
          labelFunction(value) {
            if (!value) return '';
            const text = String(value);
            if (text.length > 18) {
              return `${text.substring(0, 18)}...`;
            }
            return text;
          },
        },
        graphs: [{
          valueField: this.valueField,
          type: 'column',
          fillAlphas: 1,
          lineAlpha: 1,
          columnWidthRatio: 0.4,
          balloonText: { enabled: false },
          fillColors: titleColor,
          lineColor: titleColor,
          labelText: temPercentual ? 'R$ [[value]]\u00A0\u00A0([[percentual]]%)' : 'R$ [[value]]',
          labelPosition: 'right',
          labelOffset: 10,
          fontSize: 14,
        }],
        colors: this.colors,
        export: {
          enabled: this.showExport && !!(this.dados && this.dados.length > 0),
          fileName: this.title,
          pageOrigin: false,
          menu: [{
            class: 'export-main',
            menu: ['JPG', 'PNG', 'PDF'],
          }],
        },
        numberFormatter: {
          precision: 2,
          decimalSeparator: ',',
          thousandsSeparator: '.',
        },
      };

      const chartInstance = AmCharts.makeChart(this.id, chart);
      this.chartInstance = chartInstance;

      if (chartInstance) {
        if (!chartInstance?.categoryAxis?.addListener) return;
        const showTooltip = (ev) => {
          const dc = ev?.serialDataItem?.dataContext || {};
          const category = dc[this.categoryField] ?? ev?.value ?? '';
          const raw = dc[this.valueField];
          const numero = Number(raw);
          const percentual = dc?.percentual;
          const percentualTexto = percentual !== undefined && percentual !== null && String(percentual).length > 0
            ? ` (${percentual}%)`
            : '';

          const valorFormatado = Number.isFinite(numero) && typeof AmCharts !== 'undefined'
            ? AmCharts.formatNumber(numero, chartInstance.numberFormatter)
            : (raw ?? '');

          const html = `
            <div style="text-align:left; font-size:14px">
              <span>${category}</span><br>
              <span>R$ ${valorFormatado}${percentualTexto}</span>
            </div>
          `;

          chartInstance.showBalloon(html, titleColor, true);
        };

        const hideTooltip = () => chartInstance.hideBalloon?.();
        chartInstance.categoryAxis.addListener('rollOverItem', showTooltip);
        chartInstance.categoryAxis.addListener('rollOutItem', hideTooltip);

        this.handleRendered = () => {
          setTimeout(() => {
            if (this.chartInstance === chartInstance) {
              this.ajustarMargemEsquerda(chartInstance);
              this.extrairValoresEixoX(chartInstance);
            }
          }, 200);
        };

        chartInstance.addListener('rendered', this.handleRendered);

        this.$nextTick(() => {
          const chartElement = document.getElementById(this.id);
          if (chartElement) {
            chartElement.style.height = `${calculatedHeight}px`;
            chartInstance.validateNow();
          }
        });
      }
    },
    extrairValoresEixoX(chartInstance) {
      if (!chartInstance || !chartInstance.valueAxes || !chartInstance.valueAxes[0]) {
        this.eixoXValores = [];
        return;
      }

      const valueAxis = chartInstance.valueAxes[0];
      const valores = [];

      const chartElement = document.getElementById(this.id);
      if (chartElement) {
        const valueAxisLabels = chartElement.querySelectorAll('.amcharts-value-axis-label');
        if (valueAxisLabels && valueAxisLabels.length > 0) {
          valueAxisLabels.forEach((label) => {
            const text = label.textContent || label.innerHTML;
            if (text && text.trim()) {
              valores.push(text.trim());
            }
          });
        }
      }

      if (valores.length === 0) {
        const min = valueAxis.min || 0;
        const max = valueAxis.max || 0;

        const numLabels = 5;
        const step = numLabels > 1 ? (max - min) / (numLabels - 1) : 0;

        Array.from({ length: numLabels }).forEach((_, i) => {
          const valor = min + (step * i);
          const valorFormatado = formatarUnidades(valor, 2);
          valores.push(valorFormatado);
        });
      }

      this.eixoXValores = valores;
    },
    ajustarMargemEsquerda(chartInstance) {
      if (!chartInstance) {
        this.margemEsquerda = 0;
        return;
      }

      const chartElement = document.getElementById(this.id);
      if (!chartElement) {
        this.margemEsquerda = chartInstance.marginLeft || 0;
        return;
      }

      const categoryLabels = chartElement.querySelectorAll('.amcharts-category-axis-label');
      let maxWidth = 0;

      categoryLabels.forEach((label) => {
        const width = label.getBoundingClientRect().width || 0;
        if (width > maxWidth) {
          maxWidth = width;
        }
      });

      if (maxWidth > 0) {
        const requiredMargin = maxWidth + 20;

        if (chartInstance.marginLeft < requiredMargin) {
          chartInstance.marginLeft = requiredMargin;
          chartInstance.validateNow();
        }
      } else if (chartInstance.marginLeft < 150) {
        chartInstance.marginLeft = 150;
        chartInstance.validateNow();
      }

      this.margemEsquerda = chartInstance.marginLeft || 0;
    },
  },
  mounted() {
    window.addEventListener('AmChartCarregado', () => {
      this.isReady = true;
    });
  },
  beforeMount() {
    loadChart().then(() => {
      this.isReady = true;
      window.dispatchEvent(new Event('AmChartCarregado'));
      this.montarGrafico();
    });
  },
  beforeDestroy() {
    if (this.chartInstance) {
      if (this.handleRendered && this.chartInstance.removeListener) {
        this.chartInstance.removeListener('rendered', this.handleRendered);
      }
      if (this.chartInstance.clear) {
        this.chartInstance.clear();
      }
      this.chartInstance = null;
    }
  },
};
</script>

<style scoped>
.grafico-wrapper {
  width: 100%;
  height: 100%;
  min-height: 320px;
  max-height: 320px;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.grafico-barras-container {
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.grafico-barras-chart {
  width: 100%;
  min-width: 200px;
  position: relative;
}

.grafico-barras-chart ::v-deep .amcharts-graph-label {
  font-size: 12px !important;
}

.grafico-barras-container ::v-deep svg {
  position: relative;
}

.grafico-barras-container ::v-deep .amcharts-value-axis {
  position: sticky !important;
  bottom: 0 !important;
  z-index: 10 !important;
  background-color: transparent !important;
  padding-top: 5px !important;
}
</style>
